import { useAuthStore } from "@features/authentication/stores/authStore";
import { invalidatePostEventUnlockQueries } from "@features/payment/invalidatePostEventUnlockQueries";
import { useEventAccessPaywallModalStore } from "@features/payment/stores/eventAccessPaywallModalStore";
import type { User } from "@fitties/shared";
import { firebaseFunctionsService } from "@infrastructure/firebase/firebaseFunctionsService";

import useCurrentUser from "@shared/queries/useCurrentUser";
import QUERY_KEYS from "@shared/queryKeys";
import sharedService from "@shared/services";
import { useQueryClient } from "@tanstack/react-query";

type UnlockEventAccessResponse = {
  success?: boolean;
  eventId?: string;
  grantProfileBoost?: boolean;
  alreadyUnlocked?: boolean;
};

export function useCompleteEventUnlock() {
  const { userId } = useAuthStore();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  return async (options?: { grantProfileBoost?: boolean }) => {
    if (!userId) {
      throw new Error("Not authenticated");
    }

    await queryClient.fetchQuery({
      queryKey: [QUERY_KEYS.CURRENT_USER, userId],
      queryFn: () => sharedService.getCurrentUser(userId),
    });

    const freshUser = queryClient.getQueryData<User | null>([QUERY_KEYS.CURRENT_USER, userId]) ?? user;
    if (!freshUser?.lastKnownEventId) {
      throw new Error("Missing event context");
    }

    const response = await firebaseFunctionsService.callFunctionWithData<
      { eventId: string; grantProfileBoost?: boolean },
      UnlockEventAccessResponse
    >("unlockEventAccess", {
      eventId: freshUser.lastKnownEventId,
      ...(options?.grantProfileBoost === true ? { grantProfileBoost: true } : {}),
    });

    const unlockedEventId = response.data?.eventId ?? freshUser.lastKnownEventId;

    queryClient.setQueryData<User | null>([QUERY_KEYS.CURRENT_USER, userId], (current) => {
      if (!current) {
        return current;
      }
      const existing = current.unlockedEventIds ?? [];
      if (existing.includes(unlockedEventId)) {
        return options?.grantProfileBoost ? { ...current, isBoosted: true } : current;
      }
      return {
        ...current,
        unlockedEventIds: [...existing, unlockedEventId],
        ...(options?.grantProfileBoost ? { isBoosted: true } : {}),
      };
    });

    useEventAccessPaywallModalStore.getState().dismissForced();
    await invalidatePostEventUnlockQueries(queryClient, userId);
  };
}
